import React, { useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { useForgotPassword } from "@/queries/user";
import Cookies from "js-cookie";
import { Link } from "react-router-dom";

interface LocationState {
  from: {
    pathname: string;
  };
}

function ForgotPasswordPage() {
  const navigate = useNavigate();
  const location = useLocation();

  const [email, setEmail] = useState("");

  const [errorMessage, setErrorMessage] = useState<undefined | string>(
    undefined
  );

  const [successMessage, setSuccessMessage] = useState<undefined | string>(
    undefined
  );

  const { mutateAsync: doForgotPassword } = useForgotPassword();

  const handleRequest = async (e: React.FormEvent) => {
    e.preventDefault();
    setErrorMessage(undefined);
    //
    try {
      const { success, message } = await doForgotPassword({
        email,
      });

      if (success) {
        setSuccessMessage(message ? message : "Please check your email");
      } else if (message) {
        setErrorMessage(message);
      } else {
        setErrorMessage("Something went wrong. Please try again later.");
      }
    } catch (error) {
      setErrorMessage("Something went wrong. Please try again later.");
    }
  };

  return (
    <div className="flex flex-col items-center justify-center min-h-screen bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 dark:from-gray-950 dark:via-gray-900 dark:to-gray-950">
      <div className="flex flex-col items-center justify-center mb-10 z-10">
        <h1 className="text-3xl font-semibold text-white tracking-tight mb-1">
          Welcome to <span className="text-blue-400">RaidoHub</span>
        </h1>
        <div className="h-1 w-12 bg-blue-500 rounded mt-2"></div>
      </div>

      <Card className="w-full max-w-md border-0 shadow-xl bg-gray-800/70 backdrop-blur-sm z-10">
        <CardHeader>
          <CardTitle className="text-2xl">Forgot Password</CardTitle>
          <CardDescription>
            Please enter an email to reset your password.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {errorMessage && (
            <div className="opacity-70 dark:bg-orange-900 border dark:border-orange-600 rounded px-4 py-2 mb-4 text-sm">
              {errorMessage}
            </div>
          )}
          {successMessage && (
            <div className="opacity-70 dark:bg-green-900 border dark:border-green-600 rounded px-4 py-2 mb-4 text-sm">
              {successMessage}
            </div>
          )}

          <div className="space-y-2">
            <Label htmlFor="email">Email</Label>
            <Input
              id="email"
              placeholder=""
              required
              type="text"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="border-gray-500 bg-gray-700/50 hover:border-gray-400 
              focus:border-blue-400/30 focus:outline-none focus:ring-0 
              transition-all duration-300 focus:bg-gray-700/80 
              focus:shadow-[0_0_0_1.5px_rgba(96,165,250,0.8),0_4px_10px_rgba(37,99,235,0.35)]
              motion-safe:focus:animate-pulse-subtle"
            />
          </div>
        </CardContent>
        <CardFooter>
          <Button
            className="w-full bg-gradient-to-r from-blue-600 to-blue-500 hover:from-blue-700 hover:to-blue-600"
            onClick={handleRequest}
          >
            <div className="flex items-center justify-center gap-2 text-white">
              <span>Reset Password</span>
            </div>
          </Button>
        </CardFooter>
      </Card>

      <div className="mt-6 text-center text-gray-300 z-10">
        <p className="mb-1">Remember your password?</p>
        <Link
          to="/login"
          className="text-blue-400 hover:text-blue-300 hover:underline"
        >
          Back to login
        </Link>
      </div>
    </div>
  );
}

export default ForgotPasswordPage;
